import {
  CREDIT_REQUEST_INVOICE_FRAGMENT,
  CREDIT_REQUEST_REASON_FRAGMENT,
  CREDIT_REQUEST_UOM_FRAGMENT,
} from '@/hooks/credit-requests/credit-request-queries';
import {
  CreditRequest_OfflineDataQuery,
  CreditRequest_OfflineDataQueryVariables,
} from '@/types';
import { gql } from '@apollo/client';
import { MINIMAL_SCANNING_VARIANT_PACK_FRAGMENT } from '@/utils/scanning';
import {
  OfflineBackgroundSyncRegistryAdapter,
  OfflineCreditRequestDataAdapter,
} from '@/adapters/offline-mode';
import { OfflineSyncType } from '@/utils/offline-mode';
import { print as printQuery } from 'graphql';
import { useLazyQuery } from '@/hooks/use-lazy-query';

export const CREDIT_REQUEST_OFFLINE_QUERY = gql`
  fragment CreditRequestOfflineVariantPackFields on variants_packs {
    credit_request_config
    ...MinimalScanningVariantPackFields
  }

  fragment VariantPackCreditRequestDataFields on VariantPackCreditRequestData {
    latest_invoice {
      ...CreditRequest_SearchVariantPacksInvoices
    }
    other_creditable_uoms {
      ...CreditRequest_SearchOtherUOMs
    }
    variant_pack {
      ...CreditRequestOfflineVariantPackFields
    }
  }

  query CreditRequest_OfflineData(
    $restaurantUUID: uuid!
    $supplierUUID: uuid!
  ) {
    getCreditRequestVariantPackEligibility(
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
    ) {
      credit_request_reasons {
        credit_request_reason {
          ...GetCreditRequestReasonFields
        }
      }
      variant_pack_credit_request_data {
        ...VariantPackCreditRequestDataFields
      }
    }
  }

  ${CREDIT_REQUEST_INVOICE_FRAGMENT}
  ${CREDIT_REQUEST_UOM_FRAGMENT}
  ${CREDIT_REQUEST_REASON_FRAGMENT}
  ${MINIMAL_SCANNING_VARIANT_PACK_FRAGMENT}
`;

export const useDownloadCreditRequestData = () => {
  const getOfflineCreditRequestData = useLazyQuery<
    CreditRequest_OfflineDataQuery,
    CreditRequest_OfflineDataQueryVariables
  >(CREDIT_REQUEST_OFFLINE_QUERY, {
    fetchPolicy: 'no-cache',
  });

  const downloadCreditRequestData = async (
    restaurantUUID: string,
    supplierUUID: string,
  ) => {
    const variables = {
      restaurantUUID,
      supplierUUID,
    };

    const { data: creditRequestData } = await getOfflineCreditRequestData({
      context: {
        offlineSync: true,
      },
      variables,
    });

    const offlineCreditRequestDataAdapter = new OfflineCreditRequestDataAdapter(
      restaurantUUID,
      supplierUUID,
    );
    await offlineCreditRequestDataAdapter.save(creditRequestData);

    const backgroundSyncRegistryAdapter =
      new OfflineBackgroundSyncRegistryAdapter();
    await backgroundSyncRegistryAdapter.updateSyncMetadata(
      restaurantUUID,
      supplierUUID,
      {
        query: printQuery(CREDIT_REQUEST_OFFLINE_QUERY),
        syncType: OfflineSyncType.CreditRequest,
        variables,
      },
    );
  };

  return { downloadCreditRequestData };
};
